import { Search } from "@/components/search/Search.tsx";
import { useJobRolesSearchInfinite } from "@/queries/use-job-roles.query.ts";
import { ApiJobRoleFilter } from "@/types/job-role.types.ts";
import {
  Avatar,
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useLocation, useParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { twMerge } from "tailwind-merge";
import { JobRoleEditModal } from "@/pages/settings/jobs/roles/widgets/JobRoleEditModal.tsx";

const defaultFilter: ApiJobRoleFilter = {
  page_number: 1,
};

export function JobsPageSidePanel() {
  const { t } = useTranslation();
  const { job_role_id } = useParams<{ job_role_id?: string }>();
  const location = useLocation();
  const { openModal } = useModals();

  const [filter, setFilter] = useState<ApiJobRoleFilter>(defaultFilter);
  const [searchTerm, setSearchTerm] = useState(filter.text);
  const [isScrolled, setIsScrolled] = useState(false);

  const {
    data: jobRoles,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useJobRolesSearchInfinite(filter);
  const isDataLoading = isLoading || isFetching || isFetchingNextPage;

  const activeJobRoleId = job_role_id ? parseInt(job_role_id, 10) : undefined;
  const isFrameworkActive = location.pathname.startsWith("/settings/jobs/framework");

  const totalItems = jobRoles?.pages[0]?.metadata.total_items ?? 0;

  const { inView, ref } = useInView({
    threshold: 0.5,
    skip: isDataLoading || !hasNextPage,
    delay: 500,
  });

  useEffect(() => {
    if (isDataLoading) return;
    if (jobRoles && jobRoles.pages.length > 0 && jobRoles.pages[0]?.metadata.total_items === 0)
      return;
    if (inView) {
      fetchNextPage();
    }
  }, [inView, isDataLoading, jobRoles, fetchNextPage]);

  useDebounce(
    () => {
      setFilter((prev) => ({
        ...prev,
        text: searchTerm,
        page_number: 1,
      }));
    },
    300,
    [searchTerm]
  );

  return (
    <Stack
      className={
        "border-r w-76 h-full shrink-0 relative overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] bg-light/80"
      }
      onScroll={(event) => {
        setIsScrolled(event.currentTarget.scrollTop > 0);
      }}
    >
      <Stack
        className={twMerge(
          "sticky top-0 z-10 shrink-0 border-b border-dark/10 transition-colors",
          isScrolled && "bg-light/50 backdrop-blur-sm"
        )}
      >
        <Stack
          className={"h-16 flex items-center px-6 shrink-0"}
          direction={"horizontal"}
          gap={"sm"}
          justify={"between"}
          items={"center"}
        >
          <Text className={"text-lg font-semibold whitespace-nowrap text-ellipsis overflow-hidden"}>
            {t("plurals:job_roles_count", { count: totalItems })}
          </Text>
          <Stack direction={"horizontal"} gap={"sm"}>
            <ButtonIcon
              variant={"secondary"}
              icon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              onClick={() => {
                openModal(JobRoleEditModal);
              }}
            />
          </Stack>
        </Stack>
        <ListItem
          to={isFrameworkActive ? undefined : "/settings/jobs/framework"}
          icon={<Avatar icon={<FontAwesomeIcon icon="fa-light fa-gear" />} />}
          title={t("core:manage_framework")}
          caption={t("core:career_architecture")}
          className={twMerge(
            "p-4 px-6 border-t",
            isFrameworkActive ? "bg-light" : "hover:bg-light"
          )}
        />
        <Search
          className={"w-full"}
          innerPadding={true}
          borderless={true}
          placeholder={t("common:search")}
          value={searchTerm}
          onInput={(e) => {
            setSearchTerm(e.currentTarget.value);
          }}
          clearable={true}
          onClear={() => {
            setSearchTerm("");
            setFilter(defaultFilter);
          }}
        />
      </Stack>
      <Stack>
        {jobRoles?.pages.map((page, pageIndex) => (
          <Fragment key={`job-roles-page-${pageIndex}`}>
            {page?.items.map((role, index) => {
              const isLastItem =
                pageIndex === jobRoles.pages.length - 1 && index === page.items.length - 1;
              const isActive = activeJobRoleId === role.id;
              return (
                <Box
                  ref={isLastItem ? ref : undefined}
                  key={`job-roles-page-item-${role.id}-${pageIndex}-${index}`}
                >
                  <ListItem
                    to={isActive ? undefined : `/settings/jobs/roles/${role.id}`}
                    icon={<Avatar icon={<FontAwesomeIcon icon="fa-light fa-folder-user" />} />}
                    title={role.title}
                    caption={t("plurals:job_profiles", { count: role.profiles_count })}
                    truncate={true}
                    className={twMerge("p-4 px-6", isActive ? "bg-light" : "hover:bg-light")}
                  />
                </Box>
              );
            })}
          </Fragment>
        ))}
      </Stack>
      {isLoading && searchTerm === undefined && <Skeleton className={"w-full h-16"} />}
    </Stack>
  );
}
